import { Chart, ChartLoading, ChartNoData, ChartProps, ChartRef } from "@/components/chart";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cardVariants,
} from "@/components/ui/card";
import { ConditionalWrapper, cn } from "@/lib/utils";
import * as React from "react";
import { Button } from "./ui/button";
import { Download } from "lucide-react";
import { useThaloWeb } from "@/contexts/ThaloWebContext";
import QuickTooltip from "./quick-tooltip";
import { VariantProps } from "class-variance-authority";

interface ChartCardProps extends ChartProps {
  className?: string;
  forceLoading?: boolean;
  hideIfNoData?: boolean;
  titleToolTipText?: string;
  isLoadingCallback?: (isLoading: boolean) => void;
  hideChartOptions?: boolean;
  trendlineData?: Array<{
    sensor: string;
    metric: string;
    data: {
      slope: number;
      intercept: number;
      rate_of_change: number;
      denominator: string;
    };
  }>;
  chartRef?: React.RefObject<ChartRef>;
  highlightYAxisRange?: {
    min: number;
    max: number;
    label?: string;
    color?: string;
  };
}

export const ChartCard = ({
                            hideIfNoData = true,
                            chartRef,
                            ...props
                          }: ChartCardProps & VariantProps<typeof cardVariants>) => {
  const internalRef = React.useRef<ChartRef>(null);
  const ref = chartRef || internalRef;
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasData, setHasData] = React.useState(false);
  const { addNoDataChart } = useThaloWeb();

  React.useEffect(() => {
    if (isLoading) return;
    if (!props.title) return;
    if (!hasData && !!addNoDataChart) addNoDataChart(props.title);
  }, [hasData, isLoading]);

  // When the chart container transitions from hidden to visible (i.e. data
  // arrived and loading finished), the ECharts instance may have been
  // initialised at 0×0 inside a display:none wrapper.  useLayoutEffect
  // fires after the DOM update but before paint, so the resize happens
  // before the user ever sees the undersized chart.
  React.useLayoutEffect(() => {
    if (!isLoading && hasData) {
      ref.current?.resize();
    }
  }, [isLoading, hasData]);

  const handleDownload = () => {
    if (typeof props.title === 'string') {
      ref.current?.downloadChart(props.title);
    } else {
      ref.current?.downloadChart();
    }
  };

  return (
    <>
      <Card
        variant={props.variant}
        className={cn(
          `col-span-12 flex flex-col p-3 lg:p-4 ${
            hasData || isLoading
              ? "visible"
              : hideIfNoData
                ? "hidden"
                : "visible"
          }`,
          props.className
        )}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <ConditionalWrapper
            condition={!!props.titleToolTipText}
            wrapper={(children) => (
              <QuickTooltip text={props.titleToolTipText || ""}>
                {children}
              </QuickTooltip>
            )}
          >
            <CardTitle className="font-semibold text-lg">
              {ref.current?.overrides?.chart_title || props.title}
            </CardTitle>
          </ConditionalWrapper>
          {props.variant !== "print" && (
            <Button
              className="ml-auto flex-shrink-0"
              variant="outline"
              size="sm"
              onClick={handleDownload}
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="flex flex-1 flex-col p-0">
          {props.forceLoading && <ChartLoading height={props.height} />}
          <div className={props.forceLoading ? "hidden" : "visible"}>
            <div className={isLoading || !hasData ? "hidden" : "visible"}>
              <Chart
                ref={ref}
                {...props}
                isLoadingCallback={(isLoading: boolean) => {
                  setIsLoading(isLoading);
                  props.isLoadingCallback?.(isLoading);
                }}
                hasDataCallback={(hasData: boolean) => {
                  setHasData(hasData);
                  props.hasDataCallback?.(hasData);
                }}
                hideChartOptions={props.hideChartOptions}
                highlightYAxisRange={props.highlightYAxisRange}
              />
            </div>
            <div className={isLoading ? "visible" : "hidden"}>
              <ChartLoading height={props.height} />
            </div>
            {
              !isLoading && !hideIfNoData && !hasData && (
                <ChartNoData height={props.height} />
              )
            }
          </div>
        </CardContent>
      </Card>
    </>
  );
};